import React, { useRef, useEffect, useState, useCallback } from "react";

import classes from "./styles.module.scss";

const SyncScrollBar = ({ targetRef }) => {
    const scrollBarRef = useRef(null);
    const [innerWidth, setInnerWidth] = useState("100%");

    const updateWidth = useCallback(() => {
        if (targetRef?.current) {
            setInnerWidth(`${targetRef.current.scrollWidth}px`);
        }
    }, [targetRef]);

    // Keep inner width in sync with the target's scrollWidth
    useEffect(() => {
        const target = targetRef?.current;
        if (!target) return;

        updateWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(target);

        // Also observe children for content changes
        const mutationObserver = new MutationObserver(updateWidth);
        mutationObserver.observe(target, { childList: true, subtree: true });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [targetRef, updateWidth]);

    // Bidirectional scroll sync
    useEffect(() => {
        const scrollBar = scrollBarRef.current;
        const target = targetRef?.current;
        if (!scrollBar || !target) return;

        let isSyncing = false;

        const handleScrollBarScroll = () => {
            if (isSyncing) return;
            isSyncing = true;
            target.scrollLeft = scrollBar.scrollLeft;
            isSyncing = false;
        };

        const handleTargetScroll = () => {
            if (isSyncing) return;
            isSyncing = true;
            scrollBar.scrollLeft = target.scrollLeft;
            isSyncing = false;
        };

        scrollBar.addEventListener("scroll", handleScrollBarScroll);
        target.addEventListener("scroll", handleTargetScroll);

        return () => {
            scrollBar.removeEventListener("scroll", handleScrollBarScroll);
            target.removeEventListener("scroll", handleTargetScroll);
        };
    }, [targetRef]);

    return (
        <div className={classes.syncScrollBar} ref={scrollBarRef}>
            <div style={{ width: innerWidth, height: "1px" }} />
        </div>
    );
};

export default SyncScrollBar;
